/**
 * Batch Run Store
 *
 * Live per-member state for a running batch.
 *
 * `applyFrame` is the ONLY writer of member state. Every frame — slim
 * BATCH_PROGRESS or full run payload — goes through it, so there is exactly one
 * place that decides whether a frame is stale, duplicated or post-terminal.
 * No component reaches into another script's row.
 *
 * Ordering is per-stream, never global: run A's `sequence` is never compared
 * against run B's. Ten members are ten independent single-run orderings.
 *
 * Per TONIC-PEX-IMPL-FE-001 Tasks 4 and 6, frame contract §6.
 *
 * @module features/executionStudio/stores/batchRunStore
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import logger from '../../../shared/services/logging/logger';

/** Every member state the tracker renders (Task 6). */
export const MEMBER_STATUS = {
  PENDING: 'PENDING',
  QUEUED: 'QUEUED',
  RUNNING: 'RUNNING',
  PASSED: 'PASSED',
  PARTIAL: 'PARTIAL',
  FAILED: 'FAILED',
  CANCELLING: 'CANCELLING',
  CANCELLED: 'CANCELLED',
  OUT_OF_SCOPE: 'OUT_OF_SCOPE',
  /** Never sent to the Router. Safe to offer Re-run. */
  NOT_DISPATCHED: 'NOT_DISPATCHED',
  /** Router rejected it — show failureReason. */
  SUBMIT_FAILED: 'SUBMIT_FAILED',
  /**
   * Unknown whether it was sent. Deliberately NOT re-runnable: it may already be
   * executing, and re-running would run the customer's script twice.
   */
  SUBMIT_UNCERTAIN: 'SUBMIT_UNCERTAIN',
};

/** States no further frame may move a row out of. */
export const TERMINAL_STATUSES = new Set([
  MEMBER_STATUS.PASSED,
  MEMBER_STATUS.PARTIAL,
  MEMBER_STATUS.FAILED,
  MEMBER_STATUS.CANCELLED,
  MEMBER_STATUS.OUT_OF_SCOPE,
  MEMBER_STATUS.NOT_DISPATCHED,
  MEMBER_STATUS.SUBMIT_FAILED,
]);

/** States in which a member is actually executing. */
const LIVE_STATUSES = new Set([MEMBER_STATUS.RUNNING, MEMBER_STATUS.CANCELLING]);

/** Filter chips across the member list (Task 6). */
export const MEMBER_FILTERS = ['All', 'Passed', 'Partial', 'Failed', 'Running', 'Queued'];

const FILTER_MATCH = {
  All: () => true,
  Passed: (m) => m.status === MEMBER_STATUS.PASSED,
  Partial: (m) => m.status === MEMBER_STATUS.PARTIAL,
  Failed: (m) => m.status === MEMBER_STATUS.FAILED,
  Running: (m) => m.status === MEMBER_STATUS.RUNNING || m.status === MEMBER_STATUS.CANCELLING,
  Queued: (m) => m.status === MEMBER_STATUS.QUEUED || m.status === MEMBER_STATUS.PENDING,
};

/** A member row before any frame has arrived. */
const emptyMember = (seed = {}) => ({
  runId: seed.runId ?? null,
  /**
   * Needed for the per-run read that rebuilds state after a refresh. The
   * create-202 is the only place runId and runnableId appear together, so it is
   * captured there and kept.
   */
  runnableId: seed.runnableId ?? null,
  name: seed.name ?? '',
  order: seed.order ?? 0,
  scriptTypes: seed.scriptTypes ?? [],
  status: seed.status ?? MEMBER_STATUS.PENDING,
  progress: 0,
  // `total` is nullable by contract — null means "not known yet" and the UI
  // renders `?`. It must never be coerced to 0.
  permutations: { total: null, executed: 0, passed: 0, failed: 0 },
  startedAt: null,
  endedAt: null,
  // S3 paths from the batch-create 202 — known before anything runs, which is
  // what makes the stream.json backfill map available at submit time.
  logPath: seed.logPath ?? null,
  reportPath: seed.reportPath ?? null,
  summaryPath: seed.summaryPath ?? null,
  /** Why the Router rejected it. The only explanation a SUBMIT_FAILED has. */
  failureReason: seed.failureReason ?? null,
  // Full-frame detail, only populated while this member is expanded
  detail: null,
  detailLoading: false,
});

const INITIAL_STATE = {
  batchId: null,
  /** From the 202 — locates this run's consolidated report folder. */
  batchRunNumber: null,
  groupingId: null,
  gtid: null,
  /** Batch-level verdict from the batch-terminal frame (runid === batchId). */
  batchStatus: null,
  batchEndedAt: null,
  groupingName: null,
  projectId: null,
  projectName: null,
  startedAt: null,
  /** IANA zone the payload declares — every rendered time carries its label. */
  timeZone: 'UTC',

  // runId -> member row
  members: {},
  // Execution order of runIds — array order is execution order
  order: [],

  // Frame bookkeeping, per run. The reducer's only mutable memory.
  lastSeq: {},   // runId -> highest applied sequence
  terminal: {},  // runId -> true once the row is final

  // Only one member may be expanded at a time
  expandedRunId: null,

  // Set when the watchdog falls back to polling — silence must never look like
  // "nothing is happening"
  streamDegraded: false,

  filter: 'All',
};

export const useBatchRunStore = create((set, get) => ({
  ...INITIAL_STATE,

  /**
   * Seed the tracker from the submitted batch, before any frame arrives, so
   * every row is visible immediately in execution order.
   */
  startBatch: ({ batchId, batchRunNumber, gtid, groupingId, groupingName, projectId, projectName, timeZone, members, startedAt }) => {
    const rows = {};
    const order = [];
    const terminal = {};

    (members || []).forEach((m, i) => {
      const runId = m.runId ?? `pending-${i}`;
      rows[runId] = emptyMember({ ...m, runId, order: i + 1 });
      order.push(runId);
      // The 202 can hand back a member that is already finished — SUBMIT_FAILED,
      // NOT_DISPATCHED, OUT_OF_SCOPE. Seeding the status without the terminal
      // flag left the tracker claiming "Running" over a batch that never
      // started, and started a poll that could never make progress.
      if (TERMINAL_STATUSES.has(m.status)) terminal[runId] = true;
    });
    set({
      ...INITIAL_STATE,
      terminal,
      batchId,
      batchRunNumber,
      gtid,
      groupingId,
      groupingName,
      projectId,
      projectName,
      timeZone: timeZone || 'UTC',
      // A restore passes the original start, so the elapsed clock carries on
      // rather than restarting from zero after a refresh
      startedAt: startedAt || new Date().toISOString(),
      members: rows,
      order,
    });
    logger.info('Batch tracker started', { batchId, members: order.length });
  },

  /**
   * The one reducer. Every frame routes through here.
   *
   * Drops the frame when the row is already terminal, or when its sequence is
   * not ahead of the watermark for that run. Writes only that row.
   */
  applyFrame: (frame) => set((state) => {
    const { runid: runId, sequence } = frame || {};
    if (!runId) return state;

    // The batch-terminal frame carries runid === batchId. It is the batch's own
    // verdict, not a member — treating it as one adds a phantom row, and its
    // batch status (PARTIALLY_PASSED) is not a member terminal status, so the
    // row would never settle and completion would never fire.
    if (state.batchId && runId === state.batchId) {
      return {
        batchStatus: frame.status ?? state.batchStatus,
        batchEndedAt: frame.endedAt ?? state.batchEndedAt,
        streamDegraded: false,
      };
    }

    if (state.terminal[runId]) return state;
    if (typeof sequence === 'number' && sequence <= (state.lastSeq[runId] ?? 0)) return state;

    const existing = state.members[runId] || emptyMember({ runId });
    const status = frame.status ?? existing.status;

    // The backend's timestamps win whenever a frame carries them. It does not
    // put `startedAt` on every frame, so a member whose first RUNNING frame went
    // out before we subscribed would otherwise never get one and its clock sat at
    // 00:00. Fall back to when WE first saw it live / saw it finish — only for
    // frames observed as they happen. A REST rehydrate after a refresh is not
    // (`observed: false`): stamping "now" there would restart the clock.
    const observedAt = frame.observed === false ? null : new Date().toISOString();
    const startedAt = frame.startedAt ?? existing.startedAt
      ?? (observedAt && LIVE_STATUSES.has(status) ? observedAt : null);
    // An end without a start is no duration at all, so only stamp one we can use
    const endedAt = frame.endedAt ?? existing.endedAt
      ?? (observedAt && startedAt && TERMINAL_STATUSES.has(status) ? observedAt : null);

    const updated = {
      ...existing,
      status,
      progress: frame.progress ?? existing.progress,
      permutations: frame.permutations
        // Keep null as null — the UI renders `?`, never 0
        ? {
            total: frame.permutations.total ?? null,
            executed: frame.permutations.executed ?? 0,
            passed: frame.permutations.passed ?? 0,
            failed: frame.permutations.failed ?? 0,
          }
        : existing.permutations,
      startedAt,
      endedAt,
      // Why it stopped — a cancel reason or a dispatch failure. Never cleared
      // by a later frame that does not carry one.
      failureReason: frame.failureReason ?? existing.failureReason,
      // Full frames carry the run payload; slim frames leave detail untouched,
      // so collapsing loses detail but never loses status.
      detail: frame.testResults ?? existing.detail,
    };

    return {
      members: { ...state.members, [runId]: updated },
      order: state.order.includes(runId) ? state.order : [...state.order, runId],
      lastSeq: typeof sequence === 'number'
        ? { ...state.lastSeq, [runId]: sequence }
        : state.lastSeq,
      terminal: TERMINAL_STATUSES.has(status)
        ? { ...state.terminal, [runId]: true }
        : state.terminal,
      streamDegraded: false,
    };
  }),

  /**
   * Mark rows as cancelling during the settle window. Rows that already
   * finished are left alone — a cancelled row must never render Passed/Failed,
   * and an already-passed row must not be relabelled as cancelled.
   */
  markCancelling: (runIds) => set((state) => {
    const targets = new Set(runIds);
    const members = { ...state.members };
    targets.forEach((runId) => {
      const row = members[runId];
      if (!row || state.terminal[runId]) return;
      members[runId] = { ...row, status: MEMBER_STATUS.CANCELLING };
    });
    return { members };
  }),

  /** A member that never dispatched — a row state, never a page error (BR-33). */
  markSubmitFailed: (runId, reason) => set((state) => {
    const row = state.members[runId] || emptyMember({ runId });
    logger.warn('Batch member failed to submit', { runId, reason });
    return {
      members: { ...state.members, [runId]: { ...row, status: MEMBER_STATUS.SUBMIT_FAILED, submitError: reason } },
      terminal: { ...state.terminal, [runId]: true },
    };
  }),

  /**
   * Expand one member — only one at a time. Returns the previously expanded id
   * so the caller can unsubscribe it.
   */
  setExpanded: (runId) => {
    const previous = get().expandedRunId;
    set({ expandedRunId: runId });
    return previous;
  },

  /**
   * Collapsing unsubscribes the full stream. The sequence watermark is cleared
   * for that run so a later re-expand can replay detail from `stream.json`
   * without its frames being dropped as stale.
   */
  clearSequence: (runId) => set((state) => {
    const lastSeq = { ...state.lastSeq };
    delete lastSeq[runId];
    return { lastSeq };
  }),

  /** Flag a member's detail as loading while its snapshot is fetched. */
  setDetailLoading: (runId, detailLoading) => set((state) => {
    const row = state.members[runId];
    if (!row) return state;
    return { members: { ...state.members, [runId]: { ...row, detailLoading } } };
  }),

  setStreamDegraded: (streamDegraded) => set({ streamDegraded }),
  setFilter: (filter) => set({ filter }),
  reset: () => set({ ...INITIAL_STATE }),
}));

/* ── Derived ─────────────────────────────────────────────────────────── */

/**
 * Rows in execution order.
 *
 * Returns the stored objects as-is — deriving anything here (a live duration,
 * say) would mint new references on every call and, under `useShallow`, spin
 * the component in an update loop. Elapsed time is computed at render instead.
 */
export const selectMemberRows = (state) =>
  state.order.map(runId => state.members[runId]).filter(Boolean);

/**
 * Stat tiles. Task 6 specifies these are summed client-side from the member
 * rows — unlike the consolidated report, whose counts come straight from the
 * payload and must never be recomputed.
 */
export const selectAggregate = (state) => {
  const rows = selectMemberRows(state);
  const totals = rows.reduce((acc, m) => ({
    // A null total means "not known yet" for that member; it contributes
    // nothing rather than zero, and flags the aggregate as partial.
    total: acc.total + (m.permutations.total ?? 0),
    unknownTotals: acc.unknownTotals + (m.permutations.total == null ? 1 : 0),
    executed: acc.executed + m.permutations.executed,
    passed: acc.passed + m.permutations.passed,
    failed: acc.failed + m.permutations.failed,
  }), { total: 0, unknownTotals: 0, executed: 0, passed: 0, failed: 0 });

  const complete = rows.filter(m => state.terminal[m.runId]).length;

  return { ...totals, complete, scripts: rows.length };
};

/**
 * The grouping's outcome, rolled up from its member rows.
 *
 * Nothing serves this any more — no batch read, no report endpoint — so the
 * frontend owns it. Pass rate is **permutation-level**, not script-level: two
 * scripts at 17/21 and 13/14 are 85.7%, where a script-level count would say 0%.
 * Out-of-scope and cancelled members count toward neither.
 */
export const selectGroupingOutcome = (state) => {
  const rows = selectMemberRows(state).filter(
    (m) => m.status !== MEMBER_STATUS.OUT_OF_SCOPE && m.status !== MEMBER_STATUS.CANCELLED
  );

  const executed = rows.reduce((n, m) => n + m.permutations.executed, 0);
  const passed = rows.reduce((n, m) => n + m.permutations.passed, 0);
  const passRate = executed > 0 ? Math.round((passed / executed) * 1000) / 10 : null;

  if (rows.length === 0) return { status: 'CANCELLED', passRate };

  const anyPassed = rows.some((m) => m.status === MEMBER_STATUS.PASSED);
  const allPassed = rows.every((m) => m.status === MEMBER_STATUS.PASSED);
  const anyProgress = anyPassed || rows.some((m) => m.status === MEMBER_STATUS.PARTIAL);

  const status = allPassed ? 'PASSED' : anyProgress ? 'PARTIALLY_PASSED' : 'FAILED';
  return { status, passRate };
};

export const selectFilterCounts = (state) => {
  const rows = selectMemberRows(state);
  return MEMBER_FILTERS.reduce((acc, name) => {
    acc[name] = rows.filter(FILTER_MATCH[name]).length;
    return acc;
  }, {});
};

export const selectVisibleRows = (state) =>
  selectMemberRows(state).filter(FILTER_MATCH[state.filter] || FILTER_MATCH.All);

/* ── Selector hooks ──────────────────────────────────────────────────── */

export const useBatchMembers = () => useBatchRunStore(useShallow(selectVisibleRows));
export const useBatchAggregate = () => useBatchRunStore(useShallow(selectAggregate));
export const useBatchFilterCounts = () => useBatchRunStore(useShallow(selectFilterCounts));
export const useExpandedRunId = () => useBatchRunStore(state => state.expandedRunId);
export const useStreamDegraded = () => useBatchRunStore(state => state.streamDegraded);

export const useBatchMeta = () =>
  useBatchRunStore(useShallow(state => ({
    batchId: state.batchId,
    batchRunNumber: state.batchRunNumber,
    batchStatus: state.batchStatus,
    batchEndedAt: state.batchEndedAt,
    groupingName: state.groupingName,
    projectId: state.projectId,
    projectName: state.projectName,
    startedAt: state.startedAt,
    timeZone: state.timeZone,
    filter: state.filter,
  })));

export const useBatchRunActions = () =>
  useBatchRunStore(useShallow(state => ({
    startBatch: state.startBatch,
    applyFrame: state.applyFrame,
    markCancelling: state.markCancelling,
    markSubmitFailed: state.markSubmitFailed,
    setExpanded: state.setExpanded,
    clearSequence: state.clearSequence,
    setDetailLoading: state.setDetailLoading,
    setStreamDegraded: state.setStreamDegraded,
    setFilter: state.setFilter,
    reset: state.reset,
  })));
